import { useMemo, useCallback } from 'react';
import { RootState } from 'store';
import { useSelector } from 'react-redux';
import { Service } from 'entities/services';
import { WEST_CARD_NAME, CARD_NAME } from 'common/constants/card';

import { IFilterSettings } from 'common/constants/types/filterSettings';

import { STATE } from '../types/state';

import serviceParentIds from './serviceParentIds';
import { getServiceSettingsById } from 'common/utils/services/serviceSettings';

const PETROL_SERVICE_ID = 163;

const getServiceLeafs = (service: Service | undefined, acc: Service[]): Service[] => {
  if (!service) {
    return acc;
  }

  if (service.children && !service.children.length) {
    return [...acc, service];
  }

  return [
    ...acc,
    ...(service.children || [])
      .map(service => getServiceLeafs(service, acc)).reduce((acc, item) => {
      return [...acc, ...item];
    }, [] as Service[])
  ];
};

export const services = (state: RootState): Service[] => {
  const { services } = state[STATE];
  return services;
};

export const useServices = () => {
  const services = useSelector((state: RootState) => state[STATE].services);

  const result = useMemo(() => {
    const getItem = (item: Service): Service => ({
      ...item,
      children: (item.children || []).map(childrenItem => getItem(childrenItem)),
      name: item.name.replace(WEST_CARD_NAME, CARD_NAME)
    });

    return services.map(item => getItem(item))
  }, [services]);

  return result;
};

const mappingServices = (services: Service[]) => services
  .reduce((acc, item) => {
    acc.push({
      ...getServiceSettingsById(item.code),
      ...item,
      ...(!!item.children && item.children.length) && { children: mappingServices(item.children) }
    });
    return acc;
  }, [] as (Service & IFilterSettings)[]);

export const useServiceList = () => {
  const services = useServices();

  return useMemo(() => mappingServices(services), [services]);
};

interface ServiceId {
  [key: string]: boolean
}

const getServiceChildIds = (items: Service[]) => {
  const result: { [key: string]: ServiceId; } = {};

  const setServiceIdByParentIds = (id: string, parentIds: string[]) => parentIds
    .forEach((parentId) => { result[parentId][id] = true; });

  const traversalTree = ({ code, children }: Service, parentIds: string[] = []) => {
    result[code] = {};
    if (parentIds.length) setServiceIdByParentIds(code, parentIds);
    if (!children) return;

    children.forEach(service => { traversalTree(service, [...parentIds, code]); });
  };

  items.forEach(item => { traversalTree(item); });

  return result;
};

export const useServiceChildIds = () => {
  const services = useServices();

  return useMemo(() => getServiceChildIds(services), [services]);
};

export const useServiceParentIds = () => {
  const services = useServices();

  return useMemo(() => {
    const result: { [key: string]: ServiceId; } = {};
    const setParentIdsByServiceId = (id: string, parentIds: string[]) => parentIds
      .forEach((parentId) => { result[id][parentId] = true; });

    const traversalTree = ({ code, children }: Service, parentIds: string[] = []) => {
      result[code] = {};
      if (parentIds.length) setParentIdsByServiceId(code, parentIds);
      if (!children) return;

      children.forEach(service => { traversalTree(service, [...parentIds, code]); });
    };

    services.forEach(item => { traversalTree(item); });

    return result;
  }, [services]);
};

export const useGetChildrenServiceIds = () => {
  const serviceChildIds = useServiceChildIds();

  const getChildrenServiceIds = useCallback((id: string) => (
    serviceChildIds[id] || {}
  ), [serviceChildIds]);

  return getChildrenServiceIds;
};

export const useGetParentServiceIds = () => {
  const serviceParentIds = useServiceParentIds();

  const getParentServiceIds = useCallback((id: string) => (
    serviceParentIds[id] || {}
  ), [serviceParentIds]);

  return getParentServiceIds;
};

export const useCheckIsParent = () => {
  const serviceParentIds = useServiceParentIds();

  const checkIsParent = useCallback((childrenId: string, parentId: string) => !!serviceParentIds[childrenId][parentId],
  [serviceParentIds]);

  return checkIsParent;
};

export const useCheckIsChildren = () => {
  const serviceChildIds = useServiceChildIds();

  const checkIsChildren = useCallback((parentId: string | number, childrenId: string | number) => !!serviceChildIds[parentId][childrenId], [serviceChildIds]);

  return checkIsChildren;
};

type GetParentServiceCodesByCode = (code: string) => string[] | undefined;

export const useGetParentServiceCodesByCode = () => {
  const serviceParentIdsMap = useSelector(serviceParentIds);

  const getParentServiceCodesByCode: GetParentServiceCodesByCode = useCallback((code) => {
    const serviceParentIdMap = serviceParentIdsMap[code];
    return serviceParentIdMap ? Object.keys(serviceParentIdMap) as string[] : serviceParentIdMap;
  }, [serviceParentIdsMap]);

  return getParentServiceCodesByCode;
};

export const useSortedServices = () => {
  const services = useServices();

  const servicesOrder= useMemo(() => ([{
    code: 163, // Fuel
    children: [{
      code: 162, // disel
    }, {
      code: 40, // AdBlue
    }, {
      code: 30, // Petrol
    }, {
      code: 80, // Gas
    }]
  }] as any as Service[]), []);

  const sortedServices = useMemo(() => {
    const sortedArray = (serves: Service[], servOrder: Service[]) => {
      const result = serves.reduce((acc, service) => {
        const serviceIndex = servOrder.findIndex(item => item.code === service.code);

        if (serviceIndex > -1) {
          const { children, ...otherServiceProps } = service;

          acc.sortedServices[serviceIndex] = {
            ...otherServiceProps,
            ...children && {
              children: sortedArray(children, servOrder[serviceIndex].children || [])
            }
          };
        } else {
          acc.unsortedServices.push(service);
        }

        return acc;
      }, {
        sortedServices: [] as Service[],
        unsortedServices: [] as Service[]
      });

      return [...result.sortedServices, ...result.unsortedServices];
    }

    return sortedArray(services, servicesOrder);
  }, [
    services,
    servicesOrder
  ]);

  return sortedServices;
};

export const useFlatTreeSortedServices = () => {
  const sortedServices = useSortedServices();

  const flatTreeSortedServices = useMemo(() => {
    const getSubservices = (subservices: Service[]) => subservices
      .reduce((acc, service) => {
        const { children, ...otherServiceProps } = service;
        acc.push(otherServiceProps);
        if (service.children) {
          acc.push(...getSubservices(service.children))
        }
        return acc;
      }, [] as Service[]);

    return getSubservices(sortedServices);
  }, [
    sortedServices
  ]);

  return  flatTreeSortedServices;
};

export const useSortedServiceList = () => {
  const flatTreeSortedServices = useFlatTreeSortedServices();

  return useMemo(() => flatTreeSortedServices
    .map(({ code, name }) => ({
      key: code,
      value: name
    })).sort((firstItem, secondItem) => (
      firstItem.value.toLowerCase() > secondItem.value.toLowerCase() ? 1 : -1
    )), [flatTreeSortedServices]);
};

export const useGetServiceByCode = () => {
  const flatTreeSortedServices = useFlatTreeSortedServices();

  const getServiceByCode = useCallback((code: string) => (
    flatTreeSortedServices.find(item => item.code === code)
  ), [flatTreeSortedServices]);

  return getServiceByCode;
};


export const useSortServices = () => {
  const flatTreeSortedServices = useFlatTreeSortedServices();

  const sortServices = useCallback(<Item extends { code: string }>(items: Item[]) => (
    flatTreeSortedServices.reduce((acc, flatTreeSortedService) => {
      const foundItem = items.find(item => item.code === flatTreeSortedService.code);
  
      if (foundItem) {
        acc.push(foundItem);
      }

      return acc;
    }, [] as Item[])
  ), [
    flatTreeSortedServices
  ]);

  return sortServices;
};

export const useServicePetrol = () => {
  const serves = useServices();

  const servicePetrol = useMemo(() => (
    serves.find(item => item.code.toString() === PETROL_SERVICE_ID.toString())
  ), [serves]);

  return servicePetrol;
};

export const useServicePetrolLeafs = () => {
  const servicePetrol = useServicePetrol();
  return useMemo(() => getServiceLeafs(servicePetrol, []), [servicePetrol]);
};

export const useServiceLeafs = () => {
  const services = useServices();

  return useMemo(() => (
    services.reduce((acc, service) => ([
      ...acc,
      ...getServiceLeafs(service, [])
    ]), [] as Service[])
  ), [services]);
};

export const useSortedServiceLeafList = () => {
  const serviceLeafs = useServiceLeafs();

  return useMemo(() => serviceLeafs
    .map(({ code, name }) => ({
      key: code,
      value: name
    })).sort((firstItem, secondItem) => (
      firstItem.value.toLowerCase() > secondItem.value.toLowerCase() ? 1 : -1
    )), [serviceLeafs]);
};

